import { useState, useEffect } from "react";
import { useSession } from "@clerk/clerk-react";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import {
  Plus,
  X,
  TreeStructure,
  ArrowsClockwise,
  SpinnerGap,
} from "@phosphor-icons/react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import {
  ExtractionCondition,
  ExtractionConditionGroup,
  BUILDING_CONDITION_FIELDS,
  OPERATOR_LABELS,
} from "./types";

// Cluster type from API
interface Cluster {
  id: number;
  name: string;
}

interface ConditionGroupEditorProps {
  group: ExtractionConditionGroup;
  onChange: (group: ExtractionConditionGroup) => void;
  onRemove?: () => void;
  depth?: number;
  isRoot?: boolean;
}

// Helper to check if a condition is a group
function isConditionGroup(
  condition: ExtractionCondition | ExtractionConditionGroup
): condition is ExtractionConditionGroup {
  return 'logic' in condition && 'conditions' in condition;
}

// Check if a field is cluster-related
function isClusterField(field: string): boolean {
  return field === 'building.cluster.name' || field === 'building.clusters';
}

/**
 * ConditionGroupEditor - Recursive component for editing AND/OR condition groups
 */
export const ConditionGroupEditor = ({
  group,
  onChange,
  onRemove,
  depth = 0,
  isRoot = false,
}: ConditionGroupEditorProps) => {
  const { session } = useSession();
  const [clusters, setClusters] = useState<Cluster[]>([]);
  const [clustersLoading, setClustersLoading] = useState(false);

  // Fetch clusters when component mounts
  useEffect(() => {
    const fetchClusters = async () => {
      if (!session) return;
      setClustersLoading(true);
      try {
        const response = await fetchWithAuth(
          `${API_URL}/clusters`,
          { method: "GET" },
          session
        );
        if (response.ok) {
          const data = await response.json();
          setClusters(data);
        }
      } catch (error) {
        console.error("Error fetching clusters:", error);
      } finally {
        setClustersLoading(false);
      }
    };

    // Only fetch at root level to avoid multiple fetches
    if (depth === 0) {
      fetchClusters();
    }
  }, [session, depth]);

  const toggleLogic = () => {
    onChange({
      ...group,
      logic: group.logic === 'AND' ? 'OR' : 'AND',
    });
  };

  const addCondition = () => {
    onChange({
      ...group,
      conditions: [
        ...group.conditions,
        { field: '', operator: '==', value: '' } as ExtractionCondition,
      ],
    });
  };

  const addNestedGroup = () => {
    onChange({
      ...group,
      conditions: [
        ...group.conditions,
        {
          logic: 'AND',
          conditions: [{ field: '', operator: '==', value: '' }],
        } as ExtractionConditionGroup,
      ],
    });
  };

  const updateCondition = (
    index: number,
    updated: ExtractionCondition | ExtractionConditionGroup
  ) => {
    const newConditions = [...group.conditions];
    newConditions[index] = updated;
    onChange({ ...group, conditions: newConditions });
  };

  const removeCondition = (index: number) => {
    const newConditions = group.conditions.filter((_, i) => i !== index);
    // If removing would leave empty, keep at least one empty condition
    if (newConditions.length === 0) {
      newConditions.push({ field: '', operator: '==', value: '' } as ExtractionCondition);
    }
    onChange({ ...group, conditions: newConditions });
  };

  const bgColor = depth === 0 ? 'bg-violet-50' : depth === 1 ? 'bg-blue-50' : 'bg-neutral-50';
  const borderColor = depth === 0 ? 'border-violet-200' : depth === 1 ? 'border-blue-200' : 'border-neutral-200';

  return (
    <div className={`${bgColor} border ${borderColor} rounded-lg p-3 space-y-2`}>
      {/* Group Header */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={toggleLogic}
            className={`h-7 px-2 font-medium ${
              group.logic === 'AND'
                ? 'bg-emerald-100 text-emerald-700 border-emerald-300 hover:bg-emerald-200'
                : 'bg-amber-100 text-amber-700 border-amber-300 hover:bg-amber-200'
            }`}
          >
            <ArrowsClockwise className="h-3 w-3 mr-1" />
            {group.logic}
          </Button>
          <span className="text-xs text-neutral-500">
            {group.logic === 'AND' ? 'Alle voorwaarden moeten waar zijn' : 'Minstens één moet waar zijn'}
          </span>
        </div>

        <div className="flex items-center gap-1">
          {!isRoot && onRemove && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onRemove}
              className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Conditions */}
      <div className="space-y-2 pl-2 border-l-2 border-l-neutral-300">
        {group.conditions.map((condition, index) => (
          <div key={index}>
            {isConditionGroup(condition) ? (
              <ConditionGroupEditor
                group={condition}
                onChange={(updated) => updateCondition(index, updated)}
                onRemove={() => removeCondition(index)}
                depth={depth + 1}
              />
            ) : (
              <SingleConditionEditor
                condition={condition}
                onChange={(updated) => updateCondition(index, updated)}
                onRemove={group.conditions.length > 1 ? () => removeCondition(index) : undefined}
                clusters={clusters}
                clustersLoading={clustersLoading}
              />
            )}
          </div>
        ))}
      </div>

      {/* Add Buttons */}
      <div className="flex items-center gap-2 pt-1">
        <Button
          variant="outline"
          size="sm"
          onClick={addCondition}
          className="h-7 text-xs"
        >
          <Plus className="h-3 w-3 mr-1" />
          Voorwaarde
        </Button>
        {depth < 2 && (
          <Button
            variant="outline"
            size="sm"
            onClick={addNestedGroup}
            className="h-7 text-xs"
          >
            <TreeStructure className="h-3 w-3 mr-1" />
            Geneste groep
          </Button>
        )}
      </div>
    </div>
  );
};

interface SingleConditionEditorProps {
  condition: ExtractionCondition;
  onChange: (condition: ExtractionCondition) => void;
  onRemove?: () => void;
  clusters: Cluster[];
  clustersLoading: boolean;
}

/**
 * SingleConditionEditor - Edits a single condition (field, operator, value)
 */
const SingleConditionEditor = ({
  condition,
  onChange,
  onRemove,
  clusters,
  clustersLoading,
}: SingleConditionEditorProps) => {
  const selectedField = BUILDING_CONDITION_FIELDS.find(f => f.key === condition.field);
  const showClusterDropdown = isClusterField(condition.field);

  return (
    <div className="flex items-center gap-2 bg-white rounded-md p-2 border border-neutral-200">
      {/* Field Select */}
      <Select
        value={condition.field}
        onValueChange={(value) => onChange({ ...condition, field: value, value: '' })}
      >
        <SelectTrigger className="h-8 min-w-[180px] text-xs">
          <SelectValue placeholder="Selecteer veld..." />
        </SelectTrigger>
        <SelectContent>
          {BUILDING_CONDITION_FIELDS.map((field) => (
            <SelectItem key={field.key} value={field.key}>
              <div className="flex flex-col">
                <span>{field.label}</span>
                <span className="text-xs text-neutral-400">bijv. {field.example}</span>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Operator Select */}
      <Select
        value={condition.operator}
        onValueChange={(value) =>
          onChange({ ...condition, operator: value as ExtractionCondition['operator'] })
        }
      >
        <SelectTrigger className="h-8 w-[140px] text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {Object.entries(OPERATOR_LABELS).map(([op, label]) => (
            <SelectItem key={op} value={op}>
              {label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Value Input - Show dropdown for cluster fields, text input for others */}
      {showClusterDropdown ? (
        <Select
          value={condition.value}
          onValueChange={(value) => onChange({ ...condition, value })}
          disabled={clustersLoading}
        >
          <SelectTrigger className="h-8 flex-1 text-xs min-w-[150px]">
            {clustersLoading ? (
              <div className="flex items-center gap-1">
                <SpinnerGap className="h-3 w-3 animate-spin" />
                <span>Laden...</span>
              </div>
            ) : (
              <SelectValue placeholder="Selecteer cluster..." />
            )}
          </SelectTrigger>
          <SelectContent>
            {clusters.map((cluster) => (
              <SelectItem key={cluster.id} value={cluster.name}>
                {cluster.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        <Input
          value={condition.value}
          onChange={(e) => onChange({ ...condition, value: e.target.value })}
          placeholder={selectedField ? `bijv. ${selectedField.example}` : 'Waarde...'}
          className="h-8 flex-1 text-xs"
        />
      )}

      {/* Remove Button */}
      {onRemove && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onRemove}
          className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
        >
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
};

export default ConditionGroupEditor;
